import { useCallback, useEffect, useRef, useState } from "react"
import type { MutableRefObject } from "react"
import type { QueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import type { Core, WebViewerInstance } from "@pdftron/webviewer"
import AnnotationService from "services/annotations.service"
import { useUpdateNote, useDeleteNote } from "hooks/api/notes/notes.hook"
import { removeNoteFromCache, updateNoteInCache } from "helpers/quotationCache.helper"
import { enqueueMutation, getQueueVersionRef } from "store/mutationQueue.store"
import { noteQueueKey } from "./noteQueueKey"
import { useNoteToolStore } from "store/noteTool.store"
import { safeRender } from "../core/shadowPortal"
import type { RootEntry } from "../core/shadowPortal"
import type { TPaneBinding } from "../core/viewerPanes"
import { ShadowDomProviders } from "../ShadowDomProviders/ShadowDomProviders"
import { SelectionToolbar } from "../SelectionToolbar/SelectionToolbar"
import { TrashIcon } from "../SelectionToolbar/icons"
import type { TNote, TUpdateNoteRequest } from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"
import type { TViewerMode } from "store/viewer.store"

// Debounce (ms) for persisting a note edit. Live verification (this session, against a real
// running instance) showed Apryse already coalesces a whole drag/resize gesture and a whole
// text-edit session down to ONE `annotationChanged`/"modify" event each — fired on mouseup / on
// blur respectively, never per-frame or per-keystroke — so this debounce is a defensive backstop
// (multiple fields changing in the same tick) rather than the drag-in-progress throttle
// `useCoordinateCommit`'s 150ms exists for.
const NOTE_COMMIT_DEBOUNCE_MS = 150

export type TSelectedNote = { note: TNote; annot: Core.Annotations.Annotation }

export type NoteEditContext = {
	instanceRef: MutableRefObject<WebViewerInstance | null>
	getViewerPanes: () => TPaneBinding[]
	layoutEpoch: number
	mode: TViewerMode
	isInitialized: boolean
	numericQuotationIdRef: MutableRefObject<number | undefined>
	queryClient: QueryClient
	formatError: (error: unknown) => void
	success: (text: string) => void
	getShadowRoot: () => ShadowRoot | undefined
	// Overlay the floating delete toolbar renders into (its own container, mirroring the batch
	// selection pill's own — see core/overlayContainers.tsx).
	noteToolbarEntryRef: MutableRefObject<RootEntry | null>
}

const round2 = (n: number) => Math.round(n * 100) / 100

const coordsEqual = (a: { x0: number; y0: number; x1: number; y1: number }, b: typeof a): boolean => {
	const EPS = 0.5
	return (
		Math.abs(a.x0 - b.x0) <= EPS &&
		Math.abs(a.y0 - b.y0) <= EPS &&
		Math.abs(a.x1 - b.x1) <= EPS &&
		Math.abs(a.y1 - b.y1) <= EPS
	)
}

// Reads the two FreeText-specific style properties back off a live annotation (`FontSize` is a
// CSS-like size string, e.g. "12pt" — not a plain number; `TextColor` is an `Annotations.Color`
// object with its own `toHexString()`).
const readNoteStyleFromAnnotation = (annot: Core.Annotations.Annotation): { font_size: number; text_color: string } => {
	const freeText = annot as unknown as { FontSize: string; TextColor: Core.Annotations.Color }
	const fontSize = Math.round(parseFloat(freeText.FontSize)) || 0
	const textColor = freeText.TextColor?.toHexString?.() ?? "#000000"
	return { font_size: fontSize, text_color: textColor }
}

// REQ-395/396 note edit + delete: the ONE shared commit path every write to an existing note goes
// through (text, position/size, font size, text color), the selection tracking that shows the
// floating delete toolbar, and the delete flow itself. All four write paths share
// `noteQueueKey(note.id)`'s mutation queue (`store/mutationQueue.store.ts`) — exactly as
// `useCoordinateCommit.ts` already does for furniture/eref/ecall/page coordinate edits — so they
// can never race each other into a stale-version 400.
//
// Rather than branching on WHICH field changed (Apryse's `annotationChanged` info carries a
// `source` — live-verified this session as `"textChanged"` for a text-edit commit and `"move"` for
// a drag; a resize's own value wasn't confirmed live), every commit here DIFFS the live
// annotation's current Contents/FontSize/TextColor/geometry against `AnnotationService`'s
// last-known-good record for that note and PATCHes only whichever subset actually differs. This
// works identically whether one field changed or several did in the same tick (e.g. a font-size
// AND text-color pick from the tool-options bar), and it needs no `info.source` branch at all.
export const useNoteEdit = (ctx: NoteEditContext) => {
	const {
		instanceRef,
		getViewerPanes,
		layoutEpoch,
		mode,
		isInitialized,
		numericQuotationIdRef,
		queryClient,
		formatError,
		success,
		getShadowRoot,
		noteToolbarEntryRef,
	} = ctx

	const [selectedNote, setSelectedNoteState] = useState<TSelectedNote | null>(null)
	const selectedNoteRef = useRef<TSelectedNote | null>(null)
	const [isDeleting, setIsDeleting] = useState(false)

	const { mutateAsync: updateNoteAsync } = useUpdateNote()
	const { mutateAsync: deleteNoteAsync } = useDeleteNote()

	const setQuotationCache = useCallback(
		(
			qid: number,
			updater: (cached: AxiosResponse<TQuotationDetail> | undefined) => AxiosResponse<TQuotationDetail> | undefined,
		) => queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", qid], updater),
		[queryClient],
	)

	// --- Generic diff-and-commit (text / position / size / font size / text color) --------------
	const pendingRef = useRef<Map<number, { timer: ReturnType<typeof setTimeout>; run: () => void }>>(new Map())

	const commitNow = useCallback(
		async (noteId: number, annot: Core.Annotations.Annotation) => {
			const qid = numericQuotationIdRef.current
			const last = AnnotationService.getNoteRecord(noteId)
			if (qid == null || Number.isNaN(qid) || !last) return

			const currentText = annot.getContents()
			const { coordinates } = AnnotationService.getBackendRectFromAnnotation(annot)
			const { font_size: currentFontSize, text_color: currentTextColor } = readNoteStyleFromAnnotation(annot)

			const body: Partial<TUpdateNoteRequest> = {}
			let changed = false
			if (currentText !== last.text) {
				body.text = currentText
				changed = true
			}
			if (!coordsEqual(coordinates, last.coordinates)) {
				body.x0 = round2(coordinates.x0)
				body.y0 = round2(coordinates.y0)
				body.x1 = round2(coordinates.x1)
				body.y1 = round2(coordinates.y1)
				changed = true
			}
			if (currentFontSize > 0 && currentFontSize !== last.font_size) {
				body.font_size = currentFontSize
				changed = true
			}
			if (currentTextColor.toLowerCase() !== last.text_color.toLowerCase()) {
				body.text_color = currentTextColor
				changed = true
			}
			if (!changed) return

			const key = noteQueueKey(noteId)
			const versionRef = getQueueVersionRef(key, last.version)

			await enqueueMutation(key, versionRef.current, async () => {
				try {
					const res = await updateNoteAsync({
						quotationId: qid,
						noteId,
						body: { ...body, version: versionRef.current },
					})
					const updated = res.data
					versionRef.current = updated.version
					AnnotationService.applyNoteUpdate(updated)
					setQuotationCache(qid, (cached) => updateNoteInCache(cached, updated))
					setSelectedNoteState((prev) =>
						prev && prev.note.id === noteId ? { note: updated, annot: prev.annot } : prev,
					)
					if (selectedNoteRef.current?.note.id === noteId) {
						selectedNoteRef.current = { note: updated, annot: selectedNoteRef.current.annot }
					}
				} catch (error) {
					// Stale-version conflict or any other rejection: surface a clear error and refresh
					// rather than silently failing or overwriting someone else's edit (per the issue's
					// explicit requirement), and put the annotation back to the last known-good state so
					// the canvas doesn't keep showing an unsaved edit as if it stuck.
					formatError(error)
					queryClient.invalidateQueries({ queryKey: ["quotation", qid] })
					AnnotationService.applyNoteUpdate(last)
				}
			})
		},
		[numericQuotationIdRef, updateNoteAsync, setQuotationCache, formatError, queryClient],
	)

	const scheduleCommit = useCallback(
		(noteId: number, annot: Core.Annotations.Annotation) => {
			const pending = pendingRef.current
			const existing = pending.get(noteId)
			if (existing) clearTimeout(existing.timer)
			let done = false
			const run = () => {
				if (done) return
				done = true
				pending.delete(noteId)
				void commitNow(noteId, annot)
			}
			pending.set(noteId, { timer: setTimeout(run, NOTE_COMMIT_DEBOUNCE_MS), run })
		},
		[commitNow],
	)

	// Exposed so `useNoteCreate` can flush one more diff pass right after a create resolves, in
	// case the user typed and blurred fast enough that the "modify"/textChanged event fired before
	// the note was registered in `AnnotationService` (a real, if narrow, race between the create
	// request settling and a fast typist) — see that hook's own comment.
	const scheduleCommitForNote = scheduleCommit

	// Bind the SAME listener for both text-edit commits and move/resize commits — see the module
	// comment on why this diffs rather than branching on `info.source`. Primary-pane only: a note is
	// never mirrored into the split pane (out of scope, see AGENTS.md / issue #1321 §7).
	useEffect(() => {
		if (mode !== "editor" || !isInitialized || !instanceRef.current) return
		const { documentViewer } = instanceRef.current.Core
		const annotationManager = documentViewer.getAnnotationManager()
		if (!annotationManager) return

		const onChanged = (annotations: Core.Annotations.Annotation[], action: string, info?: { imported?: boolean }) => {
			if (action !== "modify" || info?.imported) return
			for (const annot of annotations) {
				const noteId = AnnotationService.getNoteIdForAnnotation(annot.Id)
				if (noteId == null) continue
				scheduleCommit(noteId, annot)
			}
		}
		annotationManager.addEventListener("annotationChanged", onChanged)
		const pending = pendingRef.current
		return () => {
			annotationManager.removeEventListener("annotationChanged", onChanged)
			// Flush (not drop) any pending commit on teardown, mirroring useCoordinateCommit.
			for (const { timer, run } of pending.values()) {
				clearTimeout(timer)
				run()
			}
			pending.clear()
		}
	}, [mode, isInitialized, instanceRef, scheduleCommit, layoutEpoch])

	// --- Font size / text color FROM THE TOOL-OPTIONS BAR, while a note is selected --------------
	// The bar's two pickers double as "next note" defaults (while armed) AND "edit this note"
	// controls (while a note is selected) — see `noteTool.store`'s own comment. `seededRef` tells a
	// programmatic seed-on-select apart from a real user pick: a pick always lands on a DIFFERENT
	// value than what was just seeded (clicking the currently-active swatch/size is a genuine no-op
	// PATCH-wise), so comparing against the last-seeded snapshot is enough — no separate flag needed.
	const seededRef = useRef<{ fontSize: number; textColor: string } | null>(null)

	useEffect(() => {
		return useNoteToolStore.subscribe((state, prevState) => {
			if (state.activeNoteId == null) return
			if (
				state.selectedFontSize === prevState.selectedFontSize &&
				state.selectedTextColor === prevState.selectedTextColor
			) {
				return
			}
			const seeded = seededRef.current
			if (seeded && seeded.fontSize === state.selectedFontSize && seeded.textColor === state.selectedTextColor) {
				return
			}
			const annot = AnnotationService.applyLiveNoteStyle(state.activeNoteId, {
				font_size: state.selectedFontSize,
				text_color: state.selectedTextColor,
			})
			if (annot) scheduleCommit(state.activeNoteId, annot)
		})
	}, [scheduleCommit])

	// --- Selection tracking + the floating delete toolbar -----------------------------------------
	const pendingClearRef = useRef<number | null>(null)

	const cancelPendingClear = useCallback(() => {
		if (pendingClearRef.current === null) return
		cancelAnimationFrame(pendingClearRef.current)
		pendingClearRef.current = null
	}, [])

	const clearSelection = useCallback(() => {
		cancelPendingClear()
		if (!selectedNoteRef.current) return
		selectedNoteRef.current = null
		setSelectedNoteState(null)
		seededRef.current = null
		useNoteToolStore.getState().clearActiveNote()
	}, [cancelPendingClear])

	// Clicking straight from one note to another makes Apryse fire "deselected" (note A) and then
	// "selected" (note B). Clearing on the deselect tore the tool-options row and the floating
	// toolbar down and rebuilt them a moment later — a heavy visible flicker. Deferring the clear by
	// a frame lets the incoming selection cancel it, so the bar just swaps its contents. A real
	// deselect (clicking empty space) still clears on the next frame.
	const scheduleClearSelection = useCallback(() => {
		if (!selectedNoteRef.current || pendingClearRef.current !== null) return
		pendingClearRef.current = requestAnimationFrame(() => {
			pendingClearRef.current = null
			clearSelection()
		})
	}, [clearSelection])

	useEffect(() => cancelPendingClear, [cancelPendingClear])

	useEffect(() => {
		if (mode !== "editor" || !isInitialized || !instanceRef.current) return
		const { annotationManager } = instanceRef.current.Core

		const onSelected = (annotations: Core.Annotations.Annotation[], action: string) => {
			if (action !== "selected") {
				// Deferred, so an immediately-following selection can cancel it — see
				// scheduleClearSelection.
				scheduleClearSelection()
				return
			}
			const annot = annotations[0]
			const noteId = annot ? AnnotationService.getNoteIdForAnnotation(annot.Id) : undefined
			if (noteId == null) {
				// A non-note annotation got selected (or nothing did) — a note can only ever be
				// selected alone (Apryse's edit tool replaces the selection on a plain click), so drop
				// ours if it's showing.
				clearSelection()
				return
			}
			const note = AnnotationService.getNoteRecord(noteId)
			if (!note) return
			// This selection supersedes the pending deselect of the note we just came from.
			cancelPendingClear()
			selectedNoteRef.current = { note, annot }
			setSelectedNoteState({ note, annot })
			seededRef.current = { fontSize: note.font_size, textColor: note.text_color }
			useNoteToolStore.getState().seedFromNote(note)
		}
		annotationManager.addEventListener("annotationSelected", onSelected)
		return () => annotationManager.removeEventListener("annotationSelected", onSelected)
	}, [mode, isInitialized, instanceRef, clearSelection, scheduleClearSelection, cancelPendingClear])

	const deleteSelectedNote = useCallback(async () => {
		const current = selectedNoteRef.current
		const qid = numericQuotationIdRef.current
		if (!current || qid == null || Number.isNaN(qid)) return
		const last = AnnotationService.getNoteRecord(current.note.id) ?? current.note
		const key = noteQueueKey(current.note.id)
		const versionRef = getQueueVersionRef(key, last.version)
		setIsDeleting(true)
		try {
			await enqueueMutation(key, versionRef.current, () =>
				deleteNoteAsync({ quotationId: qid, noteId: current.note.id, version: versionRef.current }),
			)
			AnnotationService.removeNote(current.note.id)
			setQuotationCache(qid, (cached) => removeNoteFromCache(cached, current.note.id))
			clearSelection()
			success("Note deleted")
		} catch (error) {
			// Stale-version conflict: the note is kept server-side — refetch rather than silently
			// failing, per the issue's explicit requirement.
			formatError(error)
			queryClient.invalidateQueries({ queryKey: ["quotation", qid] })
		} finally {
			setIsDeleting(false)
		}
	}, [numericQuotationIdRef, deleteNoteAsync, setQuotationCache, clearSelection, success, formatError, queryClient])

	// Deselect from the header's tool-options-bar close (X) button — used only while the bar is
	// showing BECAUSE a note is selected (not because the tool is armed); armed is `useNoteCreate`'s
	// own disarm.
	const deselectNote = useCallback(() => {
		const current = selectedNoteRef.current
		if (!current || !instanceRef.current) return
		instanceRef.current.Core.annotationManager.deselectAnnotation(current.annot)
		clearSelection()
	}, [instanceRef, clearSelection])

	// Position the floating toolbar above the selected note's LIVE rect (re-derived from the
	// annotation itself, not the possibly-stale cached record, so the toolbar follows a live
	// drag/resize) — same math the batch selection pill / PR #1381's highlight toolbar use.
	const positionNoteToolbar = useCallback(() => {
		const current = selectedNoteRef.current
		const entry = noteToolbarEntryRef.current
		const parent = entry?.container.parentElement
		if (!current || !entry || !parent) return
		const { pageNumber, coordinates } = AnnotationService.getBackendRectFromAnnotation(current.annot)
		const rect = AnnotationService.getClientRectFromBackendRect(pageNumber, coordinates, "primary")
		if (!rect) {
			entry.container.style.display = "none"
			return
		}
		const parentRect = parent.getBoundingClientRect()
		entry.container.style.left = `${(rect.left + rect.right) / 2 - parentRect.left}px`
		entry.container.style.top = `${rect.top - parentRect.top}px`
		entry.container.style.display = "block"
	}, [noteToolbarEntryRef])

	useEffect(() => {
		if (!selectedNote) return
		const onMove = () => positionNoteToolbar()
		const bindings = getViewerPanes().map(({ documentViewer, scrollEl }) => {
			scrollEl.addEventListener("scroll", onMove, { passive: true })
			documentViewer.addEventListener("zoomUpdated", onMove)
			return () => {
				scrollEl.removeEventListener("scroll", onMove)
				documentViewer.removeEventListener("zoomUpdated", onMove)
			}
		})
		window.addEventListener("resize", onMove)
		onMove()
		return () => {
			bindings.forEach((unbind) => unbind())
			window.removeEventListener("resize", onMove)
		}
	}, [selectedNote, positionNoteToolbar, getViewerPanes, layoutEpoch])

	// Render the floating single-action delete toolbar (mirrors PR #1381's `highlightToolbar`).
	useEffect(() => {
		const entry = noteToolbarEntryRef.current
		if (!entry) return
		const shadowRoot = getShadowRoot()
		if (!shadowRoot || !selectedNote) {
			entry.container.style.display = "none"
			safeRender(noteToolbarEntryRef, null)
			return
		}
		safeRender(
			noteToolbarEntryRef,
			<ShadowDomProviders shadowRoot={shadowRoot}>
				<SelectionToolbar
					actions={[
						{
							key: "delete",
							label: "Delete note",
							icon: <TrashIcon />,
							iconOnly: true,
							disabled: isDeleting,
							onSelect: () => void deleteSelectedNote(),
						},
					]}
				/>
			</ShadowDomProviders>,
		)
	}, [selectedNote, isDeleting, getShadowRoot, deleteSelectedNote, noteToolbarEntryRef])

	// Preview mode has no selection affordances — drop the pick, same as the furniture/eref pill.
	useEffect(() => {
		if (mode !== "editor") clearSelection()
	}, [mode, clearSelection])

	return {
		selectedNote,
		clearSelection,
		deselectNote,
		deleteSelectedNote,
		isDeleting,
		scheduleCommitForNote,
	}
}
